import {
  ContentModule,
  ModuleProperties,
  useShowId,
  useShowInstructions,
  useExternalScript,
  type GuestAuthSuccessEvent,
  type GuestExternalAuthSuccessEventName,
} from '@backstage-components/base';
import {useSubscription} from 'observable-hooks';
import {FC, useEffect, useRef} from 'react';
import {ComponentDefinition, SchemaType} from './DisneyOneidAuthDefinition';
import {OneIDInstance} from './DisneyOneid.types';

export interface IDisneyOneidAuthProps extends ModuleProperties {}

export type DisneyOneidAuthComponentDefinition = ContentModule<
  'DisneyOneidAuth',
  IDisneyOneidAuthProps & SchemaType
>;

export const DisneyOneidAuthComponent: FC<
  DisneyOneidAuthComponentDefinition
> = (definition) => {
  const {props} = definition;
  const oneIDRef = useRef<OneIDInstance | null>(null);
  const showId = useShowId();

  // This is the connection to instructions in the backstage system
  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );

  useExternalScript({
    id: 'disney-oneid-component-external',
    src: props.oneIDEnvironmentURL,
    isParsed: () =>
      typeof window !== 'undefined' && typeof window.OneID !== 'undefined',
    onload: () => {
      if (
        typeof window === 'undefined' ||
        typeof window.OneID === 'undefined' ||
        oneIDRef.current !== null
      ) {
        return;
      }

      const u = new URL(window.location.href);
      u.pathname = '/__oneid_responder.html';
      const responderPage = u.toString();

      const oneidConfig = {
        clientId: props.disneyClientId,
        // The page that will be iframed into the current page
        responderPage: responderPage,
        cssOverride: '',
      };

      // Set a ref to the OneID object
      try {
        oneIDRef.current = window.OneID.get(oneidConfig);
      } catch (reason) {
        if (
          reason instanceof Error &&
          reason.message === 'Configuration options have already been set'
        ) {
          oneIDRef.current = window.OneID.get();
        } else {
          throw reason;
        }
      }
      const instance = oneIDRef.current;

      if (instance === null) {
        return;
      }
      instance
        .init()
        .then(function (data) {
          broadcast({
            type: 'DisneyOneidAuth:on-init',
            meta: {
              isLoggedIn: data.loggedIn.toString(),
            },
          });
          // Get the guest data. The response to `init` does not inlcude the
          // guest data so this additional call is necessary to get details
          // about someone already authenticated
          if (data.loggedIn) {
            instance
              .getGuest(true)
              .then((data) => {
                broadcast({
                  type: 'DisneyOneidAuth:verify',
                  meta: {
                    email: data.profile.email ?? undefined,
                    showId,
                    moduleId: definition.mid,
                    name: data.profile.firstName,
                    swid: data.profile.swid,
                  },
                });
              })
              .catch((error) => {
                console.error(
                  'There was an error getting the guest data',
                  error
                );
                broadcast({
                  type: 'DisneyOneidAuth:on-init-error',
                  meta: {reason: `${error}`, step: 'getGuest'},
                });
              });
          }
        })
        .catch(function (error) {
          console.error('There was an error initializing OneID', error);
          broadcast({
            type: 'DisneyOneidAuth:on-init-error',
            meta: {reason: `${error}`, step: 'init'},
          });
        });

      // Need the correct types here
      instance.on('login', (data) => {
        if (!data?.profile.firstName || !data?.profile.swid) {
          broadcast({
            type: 'DisneyOneidAuth:on-failure',
            meta: {error: 'Insufficient data from "login" event'},
          });
        } else {
          broadcast({
            type: 'DisneyOneidAuth:verify',
            meta: {
              email: data.profile.email ?? undefined,
              showId,
              moduleId: definition.mid,
              name: data.profile.firstName,
              swid: data.profile.swid,
            },
          });
        }
      });
      instance.on('close', () => {
        broadcast({
          type: 'DisneyOneidAuth:on-close',
          meta: {},
        });
      });
      instance.on('logout', () => {
        broadcast({
          type: 'DisneyOneidAuth:on-logout',
          meta: {},
        });
      });
    },
  });

  useSubscription(observable, (instruction) => {
    // If the Intercom function isn't available yet we can't do much; bail out
    const oneId = oneIDRef.current;
    if (
      typeof window === 'undefined' ||
      typeof window.OneID === 'undefined' ||
      oneId === null
    ) {
      console.debug({instruction, msg: 'OneID not initialized'});
      return;
    } else if (instruction.type === 'DisneyOneidAuth:launch-login') {
      oneId
        .launchLogin()
        .then(() => {
          broadcast({
            type: 'DisneyOneidAuth:on-launch-login',
            meta: {},
          });
        })
        .catch((error) => {
          console.error('Error launching login', error);
        });
    } else if (instruction.type === 'DisneyOneidAuth:launch-register') {
      oneId
        .launchRegistration()
        .then(() => {
          broadcast({
            type: 'DisneyOneidAuth:on-launch-register',
            meta: {},
          });
        })
        .catch((error) => {
          console.error('Error launching registration', error);
        });
    } else if (instruction.type === 'DisneyOneidAuth:logout') {
      oneId.logout().then(() => {
        broadcast({
          type: 'DisneyOneidAuth:on-logout',
          meta: {},
        });
      });
    } else if (instruction.type === 'DisneyOneidAuth:success') {
      const eventName: GuestExternalAuthSuccessEventName =
        'GuestExternalAuth:success';
      const e: GuestAuthSuccessEvent = new CustomEvent(eventName, {
        detail: {attendee: instruction.meta.attendee, showId},
      });
      document.body.dispatchEvent(e);
    } else if (instruction.type === 'DisneyOneidAuth:failure') {
      broadcast({
        type: 'DisneyOneidAuth:on-failure',
        meta: {
          error:
            instruction.meta.reason ?? 'Failed to save authentication details',
        },
      });
    }
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    const onAuthSuccess = (e: GuestAuthSuccessEvent): void => {
      const {detail} = e;
      timeoutId = setTimeout(() => {
        broadcast({
          type: 'DisneyOneidAuth:on-success',
          meta: {
            showId: detail.showId,
            attendeeId: detail.attendee.id,
            attendeeEmail: detail.attendee.email,
            attendeeName: detail.attendee.name,
            attendeeTags: detail.attendee.tags.join(','),
          },
        });
      }, 50);
    };
    document.body.addEventListener('GuestExternalAuth:success', onAuthSuccess);
    return () => {
      document.body.removeEventListener(
        'GuestExternalAuth:success',
        onAuthSuccess
      );
      clearTimeout(timeoutId);
    };
  }, [broadcast]);

  return null;
};
